import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  reportConfig,
  flowStateConfig,
  resolutionConfig,
  saDashboardStats,
  reports,
  reincidencias,
  reportRules,
  getReportsByType,
  getReportsByFlowState,
  getReincidenciasByStatus,
  type ReportType,
  type ReportFlowState,
  type ReincidenciaEntry,
  type ReportEntry,
} from '@/mocks/rfwReports';

function TypeBadge({ type }: { type: ReportType }) {
  const c = reportConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function FlowBadge({ state }: { state: ReportFlowState }) {
  const c = flowStateConfig[state];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

export default function ReportesSection() {
  const [activeTab, setActiveTab] = useState<'reportes' | 'flujo' | 'reincidencias'>('reportes');
  const [typeFilter, setTypeFilter] = useState<ReportType | 'todas'>('todas');
  const [flowFilter, setFlowFilter] = useState<ReportFlowState | 'todas'>('todas');
  const [reincFilter, setReincFilter] = useState<ReincidenciaEntry['status'] | 'todas'>('todas');

  const filteredReports = typeFilter === 'todas' ? reports : getReportsByType(typeFilter);
  const displayedReports = flowFilter === 'todas' ? filteredReports : filteredReports.filter((r) => r.flowState === flowFilter);
  const filteredReinc = getReincidenciasByStatus(reincFilter);

  const tabs = [
    { id: 'reportes' as const, label: 'Reportes', icon: 'ri-flag-line', badge: reports.length },
    { id: 'flujo' as const, label: 'Flujo', icon: 'ri-git-branch-line', badge: 4 },
    { id: 'reincidencias' as const, label: 'Reincidencias', icon: 'ri-loop-left-line', badge: reincidencias.length },
  ];

  const typeFilters: { id: ReportType | 'todas'; icon: string; label: string }[] = [
    { id: 'todas', icon: 'ri-list-check-3', label: 'Todos' },
    ...Object.entries(reportConfig).map(([key, c]) => ({ id: key as ReportType, icon: c.icon, label: c.label })),
  ];

  const flowFilters: { id: ReportFlowState | 'todas'; icon: string; label: string }[] = [
    { id: 'todas', icon: 'ri-list-check-3', label: 'Todos' },
    ...Object.entries(flowStateConfig).map(([key, c]) => ({ id: key as ReportFlowState, icon: c.icon, label: c.label })),
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            SISTEMA DE REPORTES
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            La Comunidad se <span className="text-amber-600">Autorregula</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Cinco tipos de reporte, un flujo de cuatro pasos y reincidencia con memoria. Cada reporte es revisado por un moderador humano. Nada se elimina automáticamente. La comunidad RFW se protege a sí misma.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Reportes Totales', value: (saDashboardStats.totalReports / 1000).toFixed(0) + 'K', icon: 'ri-flag-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Pendientes', value: saDashboardStats.reportsPending.toString(), icon: 'ri-hourglass-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'En Revisión', value: saDashboardStats.reportsInReview.toString(), icon: 'ri-search-eye-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Tasa Resolución', value: saDashboardStats.resolutionRate, icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Tiempo Medio', value: saDashboardStats.avgResolutionTime, icon: 'ri-timer-line', color: 'text-slate-600 bg-slate-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge && tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'reportes' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {typeFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTypeFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    typeFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {displayedReports.slice(0, 6).map((r) => (
                <Link
                  key={r.id}
                  to="/reportes"
                  className="p-3.5 rounded-xl bg-background-100 border border-background-200/70 hover:border-amber-300/60 transition-all duration-200 group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <img src={r.reportedByPhotoUrl} alt={r.reportedBy} className="w-8 h-8 rounded-full object-cover shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-foreground-950 line-clamp-1">{r.reportedTargetUsername}</p>
                        <p className="text-[9px] text-foreground-500">reportado por {r.reportedByUsername}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <TypeBadge type={r.reportType} />
                      <FlowBadge state={r.flowState} />
                    </div>
                  </div>
                  <p className="text-[11px] text-foreground-700 line-clamp-2 mb-1.5">{r.reason}</p>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-foreground-400">{r.targetTypeLabel}</span>
                    <span className="text-foreground-400">{r.createdAtRelative}</span>
                  </div>
                  {r.isRepeatOffender && (
                    <div className="mt-1.5">
                      <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                        <i className="ri-loop-left-line text-[8px]"></i>
                        Reincidente ({r.repeatCount}x)
                      </span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'flujo' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(flowStateConfig).map(([key, c]) => (
                <div key={key} className="p-5 rounded-2xl bg-background-100 border border-background-200/70 text-center relative overflow-hidden">
                  <div className={`absolute top-0 left-0 right-0 h-1 ${c.bgColor}`}></div>
                  <div className={`w-12 h-12 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-3 mt-2`}>
                    <i className={`${c.icon} text-xl ${c.color}`}></i>
                  </div>
                  <p className="text-sm font-extrabold text-foreground-950 mb-1">{c.label}</p>
                  <p className="text-[11px] text-foreground-500 leading-relaxed">{c.description}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {flowFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFlowFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    flowFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              {reports.filter((r) => flowFilter === 'todas' || r.flowState === flowFilter).slice(0, 4).map((r) => (
                <FlujoCard key={r.id} report={r} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reincidencias' && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {([
                { id: 'todas' as const, label: 'Todas' },
                { id: 'vigilancia' as const, label: 'En Vigilancia', icon: 'ri-radar-line' },
                { id: 'reincidente' as const, label: 'Reincidentes', icon: 'ri-loop-left-line' },
                { id: 'sancionado' as const, label: 'Sancionados', icon: 'ri-forbid-line' },
              ]).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setReincFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    reincFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredReinc.map((r) => (
                <div key={r.id} className="p-4 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-2.5 mb-3">
                    <img src={r.reportedTargetPhotoUrl} alt={r.reportedTargetUsername} className="w-9 h-9 rounded-full object-cover opacity-70 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-foreground-950 line-clamp-1">{r.reportedTargetUsername}</p>
                      <p className="text-[9px] text-foreground-500">{r.totalReports} reportes · Último: {r.lastReportDateRelative}</p>
                    </div>
                    <span className={`shrink-0 text-[9px] px-2 py-0.5 rounded-full font-medium ${
                      r.status === 'sancionado' ? 'bg-red-100 text-red-700' :
                      r.status === 'reincidente' ? 'bg-amber-100 text-amber-700' :
                      'bg-sky-100 text-sky-700'
                    }`}>
                      {r.statusLabel}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {r.reportTypes.map((t) => {
                      const c = reportConfig[t];
                      return (
                        <span key={t} className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${c.bgColor} ${c.color}`}>{c.label}</span>
                      );
                    })}
                  </div>
                  <div className="space-y-0.5">
                    {r.previousSanctions.map((s, si) => (
                      <p key={si} className="text-[9px] text-foreground-500 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        {s}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {reportRules.map((rule) => (
            <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
              <div className={`w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2`}>
                <i className={`${rule.icon} text-lg ${rule.color}`}></i>
              </div>
              <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
              <p className="text-[11px] text-foreground-500">{rule.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/reportes"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-flag-line"></i>
            Ver Sistema de Reportes
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-amber-100/70 border border-amber-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-eye-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            No eliminar automáticamente. Requiere revisión.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            En RFW cada reporte pasa por un moderador humano antes de cualquier acción. Nada se borra por sistema, nada se bloquea sin contexto. La autorregulación comunitaria funciona cuando hay criterio humano detrás de cada decisión. Los reportes son una herramienta de la comunidad, no un arma automática.
          </p>
        </div>
      </div>
    </section>
  );
}

function FlujoCard({ report: r }: { report: ReportEntry }) {
  return (
    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-background-100 border border-background-200/70">
      <div className="relative shrink-0">
        <img src={r.reportedByPhotoUrl} alt={r.reportedBy} className="w-10 h-10 rounded-full object-cover" />
        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${reportConfig[r.reportType].bgColor} flex items-center justify-center border-2 border-background-100`}>
          <i className={`${reportConfig[r.reportType].icon} text-[8px] ${reportConfig[r.reportType].color}`}></i>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <TypeBadge type={r.reportType} />
          <FlowBadge state={r.flowState} />
          <span className="text-[10px] text-foreground-400 ml-auto shrink-0">{r.createdAtRelative}</span>
        </div>
        <p className="text-[11px] font-bold text-foreground-950 line-clamp-1">{r.reportedTargetUsername}</p>
        <p className="text-[10px] text-foreground-500 line-clamp-1">{r.reason.slice(0, 100)}</p>
        {r.assignedReviewer && (
          <p className="text-[9px] text-foreground-400 mt-0.5">
            <i className="ri-shield-user-line text-[8px] mr-0.5"></i>{r.assignedReviewer}
          </p>
        )}
      </div>
    </div>
  );
}